import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as ClaudeCodeIcon } from 'Images/claude_code.svg';
import { ReactComponent as ClaudeDesktopIcon } from 'Images/claude_desktop.svg';

import SelectionTabs from './components/SelectionTabs';
import terminalClients from './terminalClients';
import {
  Wrapper,
  Section,
  SectionTitle,
  SectionText,
  CommandCard,
  CommandTextContent,
  CommandLine
} from './styled';
import PromptCard from './components/PromptCard';

const clientOptions = [
  {
    value: 'claudeCode',
    label: 'Claude Code',
    icon: ClaudeCodeIcon
  },
  {
    value: 'claudeDesktop',
    label: 'Claude Desktop',
    icon: ClaudeDesktopIcon
  }
];

const authenticationOptions = [
  {
    value: 'tokenAuthentication',
    label: 'Token Authentication'
  },
  {
    value: 'usernamePassword',
    label: 'Username / Password'
  }
];

const configFileOptions = [
  {
    value: 'macOS',
    label: '~/Library/Application Support/Claude/claude_desktop_config.json'
  },
  {
    value: 'Windows',
    label: '%APPDATA%\\Claude\\claude_desktop_config.json'
  },
  {
    value: 'Linux',
    label: '~/.config/Claude/claude_desktop_config.json'
  }
];

const ClientConfiguration = ({
  mcpServerUrl,
  faradayInstanceUrl
}) => {
  const [selectedClient, setSelectedClient] = useState('claudeCode');
  const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] = useState('tokenAuthentication');

  const commandText = useMemo(() => {
    const selectedTerminalClient = terminalClients[selectedClient];
    const selectedCommandBuilder = selectedTerminalClient?.[selectedAuthenticationMethod];

    if (!selectedCommandBuilder) {
      return '';
    }

    return selectedCommandBuilder({
      mcpServerUrl,
      faradayInstanceUrl
    }).trim();
  }, [
    faradayInstanceUrl,
    mcpServerUrl,
    selectedAuthenticationMethod,
    selectedClient
  ]);

  const commandLines = useMemo(
    () => commandText.split('\n'),
    [commandText]
  );

  const promptText = useMemo(() => (
    <CommandTextContent>
      {commandLines.map((line, index) => (
        <CommandLine key={ `${selectedClient}-${selectedAuthenticationMethod}-${index}` }>
          {line}
        </CommandLine>
      ))}
    </CommandTextContent>
  ), [commandLines, selectedAuthenticationMethod, selectedClient]);

  return (
    <Wrapper>
      <Section>
        <SectionTitle>Select Client</SectionTitle>
        <SelectionTabs
          ariaLabel="Client selection"
          onChange={ setSelectedClient }
          options={ clientOptions }
          value={ selectedClient }
        />
      </Section>
      <Section>
        <SectionTitle>Authentication Method</SectionTitle>
        <SelectionTabs
          ariaLabel="Authentication method selection"
          onChange={ setSelectedAuthenticationMethod }
          options={ authenticationOptions }
          value={ selectedAuthenticationMethod }
        />
      </Section>
      <Section>
        <SectionTitle>Run in Terminal</SectionTitle>
        <CommandCard
          copyValue={ commandText }
          text={ promptText }
        />
      </Section>
      {selectedClient === 'claudeDesktop' && (
        <Section>
          <SectionTitle>Config file locations</SectionTitle>
          {configFileOptions.map(({ value, label }) => (
            <PromptCard
              key={ value }
              copyValue={ label }
              text={ label }
              copyLabel={ value }
            />
          ))}
          <SectionText>
            After editing the config, fully quit and restart Claude Desktop.
          </SectionText>
        </Section>
      )}
    </Wrapper>
  );
};

ClientConfiguration.propTypes = {
  mcpServerUrl: PropTypes.string,
  faradayInstanceUrl: PropTypes.string
};

ClientConfiguration.defaultProps = {
  mcpServerUrl: 'https://mcp.apps.faradaysec.com/mcp',
  faradayInstanceUrl: 'https://your-faraday-instance.com'
};

export default ClientConfiguration;
